/**
 * Shared helpers for the mock-first services of People & Access.
 *
 * No backend contract exists yet (ARP-1427 / ARP-1480), so every service in
 * `src/services/` resolves the platform envelope `{ status_code, body, meta }`
 * from a module-level seed and carries a `TODO(CT-xxxx / backend)` marker per
 * function. One definition of the envelope and the filter matcher so the
 * services cannot drift apart.
 */

/** A deep copy, so callers cannot mutate the mock store through what they receive (like a real API). */
export const clone = (value) =>
  typeof structuredClone === 'function' ? structuredClone(value) : JSON.parse(JSON.stringify(value));

export const envelope = (body, meta = {}) => ({ status_code: 200, body, meta });

/** 404-style envelope so callers can tell “not found” from a failed request. */
export const notFoundEnvelope = (description = 'Not found') => ({
  status_code: 404,
  body: null,
  meta: {},
  msg_code: [{ code: 'NOT_FOUND', description }],
});

/**
 * Does `item` satisfy every non-empty entry of `filters`? A filter value may be a
 * scalar or an array (multi-select); the item field may be a scalar or an array.
 */
export const matchesFilters = (item, filters = {}) =>
  Object.entries(filters).every(([key, value]) => {
    if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) return true;
    const field = item[key];
    if (Array.isArray(value)) {
      return Array.isArray(field) ? value.some((v) => field.includes(v)) : value.includes(field);
    }
    return Array.isArray(field) ? field.includes(value) : field === value;
  });

/** Non-2xx envelope (403 forbidden, 409 conflict, 422 invalid …) with one message. */
export const errorEnvelope = (status_code, code, description) => ({
  status_code,
  body: null,
  meta: {},
  msg_code: [{ code, description }],
});

/**
 * Case/accent-insensitive “contains” over the given `fields` of `item`. A field
 * accessor may be a key or a function `(item) => string | string[]`.
 */
export const matchesSearch = (item, query, fields = []) => {
  const needle = normalizeText(query);
  if (!needle) return true;
  return fields.some((field) => {
    const raw = typeof field === 'function' ? field(item) : item[field];
    return (Array.isArray(raw) ? raw : [raw]).some((v) => normalizeText(v).includes(needle));
  });
};

/** Stable sort by `accessor(item)`; strings compare locale-aware, null/'' sort last. */
export const sortItems = (items, accessor, direction = 'asc') => {
  const dir = direction === 'desc' ? -1 : 1;
  return [...items].sort((a, b) => {
    const av = accessor(a);
    const bv = accessor(b);
    const aEmpty = av == null || av === '';
    const bEmpty = bv == null || bv === '';
    if (aEmpty || bEmpty) return aEmpty === bEmpty ? 0 : aEmpty ? 1 : -1;
    if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv) * dir;
    return (av < bv ? -1 : av > bv ? 1 : 0) * dir;
  });
};

/** Slice one page and build the pagination `meta` the platform returns (page and page_size are clamped to >= 1). */
export const paginate = (items, params = {}) => {
  const page = Math.max(1, Math.floor(Number(params.page) || 1));
  const page_size = Math.max(1, Math.floor(Number(params.page_size) || 10));
  const total = items.length;
  const start = (page - 1) * page_size;
  return {
    items: items.slice(start, start + page_size),
    meta: { total, page, page_size, total_pages: Math.max(1, Math.ceil(total / page_size)) },
  };
};

function normalizeText(value) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();
}
